import { UpdateBalanceSheetBundleConfigurationInput } from '@/types/schema';

import { diffUnordered } from '../diff/diffUnordered';
import { BundleSaveDescriptor } from '../presentationSave.types';
import { createFlatConfigDescriptor } from './flatConfigDescriptor';

/**
 * The balance sheet bundle: which sections and columns the sheet shows.
 *
 * `columnOrder` is deliberately absent. It is write-only — the backend reads it
 * to order the response and never returns it — so the form never holds it and
 * there is nothing to diff. The create path sends an empty array to ask for the
 * default ordering, and a save that leaves the field out keeps whatever the
 * server already has.
 */
export const balanceSheetBundleDescriptor: BundleSaveDescriptor =
  createFlatConfigDescriptor({
    field: 'balanceSheetBundleConfiguration',
    read: (bundle) => bundle.balanceSheetBundleConfiguration,
    diff: (previous, next) => {
      const update: UpdateBalanceSheetBundleConfigurationInput = {};

      // Nullable, so clearing it needs the flag rather than a null value.
      if (previous.displayName !== next.displayName) {
        if (next.displayName) {
          update.displayName = next.displayName;
        } else {
          update.clearDisplayName = true;
        }
      }
      if (previous.showInEstate !== next.showInEstate) {
        update.showInEstate = next.showInEstate;
      }
      if (
        previous.separateInEstateDirectlyHeld !==
        next.separateInEstateDirectlyHeld
      ) {
        update.separateInEstateDirectlyHeld = next.separateInEstateDirectlyHeld;
      }
      if (previous.separateInEstateTrusts !== next.separateInEstateTrusts) {
        update.separateInEstateTrusts = next.separateInEstateTrusts;
      }
      if (previous.showOutOfEstate !== next.showOutOfEstate) {
        update.showOutOfEstate = next.showOutOfEstate;
      }
      if (
        previous.separateOutOfEstateTrusts !== next.separateOutOfEstateTrusts
      ) {
        update.separateOutOfEstateTrusts = next.separateOutOfEstateTrusts;
      }
      if (previous.hideSmallValues !== next.hideSmallValues) {
        update.hideSmallValues = next.hideSmallValues;
      }
      if (previous.showSummaryMetrics !== next.showSummaryMetrics) {
        update.showSummaryMetrics = next.showSummaryMetrics;
      }
      if (
        previous.showExpandedAssetCategories !==
        next.showExpandedAssetCategories
      ) {
        update.showExpandedAssetCategories = next.showExpandedAssetCategories;
      }
      if (
        previous.includeDeathBenefitInTotals !==
        next.includeDeathBenefitInTotals
      ) {
        update.includeDeathBenefitInTotals = next.includeDeathBenefitInTotals;
      }
      if (previous.includeExportSlide !== next.includeExportSlide) {
        update.includeExportSlide = next.includeExportSlide;
      }

      // Replaced wholesale rather than appended: hidden columns are a set, and
      // the input takes the whole list. `diffUnordered` only answers whether the
      // membership changed, so hiding the same columns in a different order
      // writes nothing.
      if (diffUnordered(previous.hiddenColumns, next.hiddenColumns)) {
        update.hiddenColumns = next.hiddenColumns;
      }

      return Object.keys(update).length > 0 ? update : null;
    },
    toPayload: (id, update) => ({
      updateBalanceSheetBundleConfiguration: { id, update },
    }),
  });
